import * as React from 'react';
import canCreateTaskInFolder from '../utils/canCreateTaskInFolder';
import DragToEndOfList from './DragToEndOfList';
import EmptySpacer from './EmptySpacer';
import FolderPicker from './Folders/FolderPicker';
import FRE from './FRE';
import getSelectedFolderView from '../selectors/getSelectedFolderView';
import getSelectedFolderViewState from '../selectors/getSelectedFolderViewState';
import getTodoFolderStore from '../selectors/getTodoFolderStore';
import ListOrDropHint from './ListOrDropHint';
import QuickAdd from './QuickAdd';
import RecentlyCompletedTasks from './RecentlyCompletedTasks';
import TaskCreateDroppable from './TaskCreateDroppable';
import TodoAppLink from './TodoAppLink';
import TodoContextMenu from './TodoContextMenu';
import TodoList from './TodoList';
import { isFeatureEnabled } from 'owa-feature-flags';
import { loadPanel, panelClosed } from '../actions/todoPanelStoreActions';
import { TodoPanelSource } from '../store/schema/TodoPanelSource';
import { observer } from 'mobx-react';
import { KeyCodes } from '@uifabric/utilities';

const styles = require('./TodoPanel.scss');
const classNames = require('classnames');

export interface TodoPanelProps {
    /**
     * If set to false, the folder picker won't be displayed in the panel contents.
     */
    hideFolderPicker?: boolean;
    source: TodoPanelSource;
    parentSessionId?: string;
    closePanel?: () => void;
}

@observer
export default class TodosPanel extends React.Component<TodoPanelProps, {}> {
    componentDidMount() {
        loadPanel(this.props.source, this.props.parentSessionId);
    }

    componentWillUnmount() {
        panelClosed();
    }

    render() {
        const folderView = getSelectedFolderView();
        const folderViewState = getSelectedFolderViewState();
        const contextMenuState = folderViewState ? folderViewState.contextMenuViewState : null;
        const orderedList = getTodoFolderStore().orderedList;
        const canShowFolderTree =
            this.props.hideFolderPicker !== true && orderedList !== null && orderedList.length > 1;
        const canAddTasks = folderView ? canCreateTaskInFolder(folderView.folderId) : false;

        /**
         * We add the droppable on the largest region possible to minimize the area where
         * we lose track of the email being dragged from the list view over to the panel.
         */
        return (
            <div className={styles.keyDownContainer} onKeyDown={this.onKeyDown}>
                <TaskCreateDroppable
                    classNames={classNames(
                        styles.container,
                        isFeatureEnabled('fwk-o365shellheader') &&
                            !isFeatureEnabled('cal-time-panel') &&
                            'shellDarkMode'
                    )}>
                    {canShowFolderTree && (
                        <div className={styles.folderPicker}>
                            <FolderPicker commandButtonTextClassName={styles.folderPickerText} />
                        </div>
                    )}

                    <ListOrDropHint>
                        <FRE />
                        {canAddTasks && <QuickAdd />}
                        <div className={styles.listContent}>
                            {folderView && <TodoList todoIds={folderView.activeTaskIds} />}
                            {folderView && <DragToEndOfList />}
                            {folderView && <RecentlyCompletedTasks />}
                        </div>
                        {contextMenuState && <TodoContextMenu />}
                        <EmptySpacer />
                    </ListOrDropHint>

                    <TodoAppLink />
                </TaskCreateDroppable>
            </div>
        );
    }

    private onKeyDown = (target: React.KeyboardEvent<EventTarget>) => {
        switch (target.keyCode) {
            case KeyCodes.escape:
                // If escape handling got to here, close the panel
                this.props.closePanel && this.props.closePanel();
                break;
        }
    };
}
